import { Stack } from "@mui/material";
import { usePayPointScanViewQuery } from "Api/Queries/PayPoints/usePayPointScanViewQuery";
import { AnonymousCollectorStepOne } from "Components/AnonymousCustomer/Payment/Collector/AnonymousCollectorStepOne";
import { SignInPrompt } from "Components/AnonymousCustomer/Payment/PaymentDetail/SignInPrompt";
import { AnonymousLayout } from "Components/Layout/AnonymousLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { PayPointMode } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useLocation, useParams } from "react-router-dom";

const PageResources = Resources.Customer.Pay;

export const AnonymousCollectorPaymentPage = () => {
  const { t } = useResource();
  const { id: payPointPublicID } = useParams<{ id: string }>();
  const {
    data: payPoint,
    isLoading,
    isError,
  } = usePayPointScanViewQuery(payPointPublicID);

  if (!payPointPublicID) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Error.InvalidPaypointId)}</ErrorText>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (isError || !payPoint) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Errors.DataLoadingError)}</ErrorText>
      </Layout>
    );
  }

  if (payPoint.mode !== PayPointMode.Collector) {
    return (
      <Layout>
        <ErrorText>
          {t(PageResources.AnonymousCollector.UnsupportedMode)}
        </ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      <AnonymousCollectorStepOne payPoint={payPoint} />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();
  const location = useLocation();
  const returnUrl = `${location.pathname}${location.search}`;

  return (
    <AnonymousLayout title={t(PageResources.Title)} isFullHeight>
      <StyledPageWrapper marginX="auto">
        <Stack gap={3} height="100%">
          <Stack pt={4} flexGrow={1}>
            {children}
          </Stack>
          <SignInPrompt returnUrl={returnUrl} />
        </Stack>
      </StyledPageWrapper>
    </AnonymousLayout>
  );
};
